import { fetchClient } from "@/api/api";
import type {
	createUserInput,
	createUserOutput,
	getAllUsersInput,
	getAllUsersOutput,
	getUserInput,
	getUserOutput,
	getUserWalletOutput,
	putUserWalletInfoBody,
	putUserWalletInfoOutput,
	updateUserInput,
	updateUserOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import { PAYMENTS_SERVICE_KEYS, PLAYERS_AUCTION_KEYS } from "@/services";
import { currentUserAtom } from "@/store/login";
import type { ApiError } from "@/utils/fetching";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { invalidateQueries } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { USERS_SERVICE_KEYS } from "./keys";

export const useGetAllUsers: CustomQueryHook<
	getAllUsersOutput,
	getAllUsersInput
> = () => {
	return useQuery<getAllUsersOutput, ApiError>({
		queryKey: USERS_SERVICE_KEYS.useGetAllUsers(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/users/", {
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
	});
};

export const useCreateUser: CustomMutationHook<
	createUserInput,
	createUserOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<createUserOutput, ApiError, createUserInput>({
		mutationKey: USERS_SERVICE_KEYS.useCreateUser(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/users/", {
				body,
				throwOnError: true,
			});
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(USERS_SERVICE_KEYS.useGetAllUsers());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetUser = (
	options?: UseQueryOptions<getUserOutput, getUserInput>,
) => {
	const currentUser = useAtomValue(currentUserAtom);
	const user_id = currentUser?.id;

	return useQuery<getUserOutput, getUserInput>({
		...options,
		queryKey: USERS_SERVICE_KEYS.useGetUser({ user_id: user_id! }),
		enabled: !!user_id,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/users/{user_id}", {
				params: {
					path: { user_id },
				},
				throwOnError: true,
			});

			if (error) throw error;
			return data;
		},
	});
};

export const useUpdateUser = () => {
	const { toastApiError } = useApiError();
	const currentUser = useAtomValue(currentUserAtom);
	const user_id = currentUser?.id;

	return useMutation<updateUserOutput, ApiError, updateUserInput>({
		mutationKey: USERS_SERVICE_KEYS.useUpdateUser(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/users/{user_id}",
				{
					params: { path: { user_id } },
					body,
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(USERS_SERVICE_KEYS.useGetUser({ user_id: user_id! }));
			invalidateQueries(USERS_SERVICE_KEYS.useGetAllUsers());
			// Invalidate players auction init data to refresh transfer multiplier in Player Auction form
			invalidateQueries(PLAYERS_AUCTION_KEYS.useGetPlayersAuctionInit());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetUserWallet: CustomQueryHook<getUserWalletOutput> = (
	options,
) => {
	const currentUser = useAtomValue(currentUserAtom);
	const user_id = currentUser?.id;

	return useQuery({
		...options,
		queryKey: USERS_SERVICE_KEYS.useGetUserWallet({ user_id: user_id! }),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/users/{user_id}/wallet",
				{
					params: {
						path: { user_id },
					},
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		refetchInterval: 30_000,
	});
};

/**
 * Admin variant of {@link useGetUser} that reads an arbitrary user (including
 * `wallet_info`) via GET /v1/fc/users/{user_id}.
 */
export const useGetUserById = (
	userId: string,
	options?: Omit<
		UseQueryOptions<getUserOutput, ApiError>,
		"queryKey" | "queryFn"
	>,
) => {
	return useQuery<getUserOutput, ApiError>({
		...options,
		queryKey: USERS_SERVICE_KEYS.useGetUser({ user_id: userId }),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/users/{user_id}", {
				params: { path: { user_id: userId } },
				throwOnError: true,
			});

			if (error) throw error;
			return data;
		},
	});
};

export const useUpdateUserWalletInfo: CustomMutationHook<
	putUserWalletInfoBody,
	putUserWalletInfoOutput
> = () => {
	const { toastApiError } = useApiError();
	const currentUser = useAtomValue(currentUserAtom);
	const user_id = currentUser?.id;

	return useMutation<putUserWalletInfoOutput, ApiError, putUserWalletInfoBody>({
		mutationKey: USERS_SERVICE_KEYS.usePutUserWalletInfo(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.PUT(
				"/v1/fc/users/{user_id}/wallet-info/",
				{
					params: { path: { user_id } },
					body,
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(USERS_SERVICE_KEYS.useGetUser({ user_id: user_id! }));
			invalidateQueries(
				USERS_SERVICE_KEYS.useGetUserWallet({ user_id: user_id! }),
			);
			invalidateQueries(PAYMENTS_SERVICE_KEYS.useGetAllPayments());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

/**
 * Admin variant of {@link useUpdateUserWalletInfo} that overwrites the
 * wallet/bank info of an arbitrary user via PUT /v1/fc/users/{user_id}/wallet-info/.
 */
export const useAdminUpdateUserWalletInfo = (userId: string) => {
	const { toastApiError } = useApiError();

	return useMutation<putUserWalletInfoOutput, ApiError, putUserWalletInfoBody>({
		mutationKey: USERS_SERVICE_KEYS.usePutUserWalletInfo({ user_id: userId }),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.PUT(
				"/v1/fc/users/{user_id}/wallet-info/",
				{
					params: { path: { user_id: userId } },
					body,
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(USERS_SERVICE_KEYS.useGetUser({ user_id: userId }));
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};
